import { Injectable, Input } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { observable } from 'mobx-angular';
import { filter, map, mergeMap } from 'rxjs/operators';

export enum SideBar {
  None,
  Default,
  Stepper,
}

export interface StepperRoute {
  id: string;
  step: number;
  title: string;
  fullPath: string;
}

@Injectable({
  providedIn: 'root',
})
export class SideBarService {
  @observable sideBarState = SideBar.None;
  @observable title: string = 'get started';
  @Input() showRecommendedProduct: boolean = true;
  @Input() showNonRecommendedProduct: boolean = false;

  stepperRoutes: StepperRoute[] = [];
  currentRouteId: string = '';

  constructor(private router: Router, private activatedRoute: ActivatedRoute) {
    this.initStepperRoutes();

    router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .pipe(map(() => activatedRoute))
      .pipe(
        map((route) => {
          while (route.firstChild) {
            route = route.firstChild;
          }
          return route;
        }),
      )
      .pipe(filter((route) => route.outlet === 'primary'))
      .pipe(mergeMap((route) => route.data))
      .subscribe((data) => {
        this.sideBarState =
          data.sideBarState !== null ? data.sideBarState : SideBar.Default;
        this.currentRouteId = data.id;
        this.title = data.title;
      });
  }

  initStepperRoutes() {
    this.stepperRoutes = [];
    this.router.config.forEach((route) => {
      if (route.data?.sideBarState === SideBar.Stepper) {
        this.stepperRoutes.push({
          id: route.data?.id,
          step: route.data?.step,
          title: route.data?.title,
          fullPath: route.path || '',
        });
      }

      if (route.children) {
        const parentPath = route.path;
        const routeWithSidebarStepper = route.children.filter(
          (subRoute) => subRoute.data?.sideBarState === SideBar.Stepper,
        );
        routeWithSidebarStepper.forEach((route) => {
          this.stepperRoutes.push({
            id: route.data?.id,
            step: route.data?.step,
            title: route.data?.title,
            fullPath: `${parentPath}/${route.path}`,
          });
        });
      }
    });
  }

  get filteredStepperRoutes() {
    return this.stepperRoutes
      .filter((route) => {
        if (
          route.id === 'recommended-products' &&
          !this.showRecommendedProduct
        ) {
          return false;
        }

        if (
          route.id === 'non-recommended-products' &&
          !this.showNonRecommendedProduct
        ) {
          return false;
        }

        return true;
      })
      .map((route, index) => {
        return {
          ...route,
          step: index + 1,
        };
      });
  }

  get currentStep() {
    //using -1 as invalid
    return (
      this.filteredStepperRoutes.find(
        (route) => route.id === this.currentRouteId,
      )?.step || -1
    );
  }
}
